import { Grid } from "@mui/material";
import { FormikProps, FormikValues, getIn } from "formik";
import CustomSelect from "@/components/CustomSelect";
import Input from "@/components/Input";
import { COUNTRIES_LIST, STATE_LIST } from "@/constants";
import {
  ZIP_CODE_BASE_DIGITS,
  ZIP_CODE_MAX_DIGITS,
  ZIP_CODE_MAX_LENGTH,
} from "@/constants/address-constant";
import FormSectionCard from "./FormSectionCard";

export interface AddressSectionProps<T extends FormikValues> {
  formik: FormikProps<T>;
  formikKey: string;
  title?: string;
  required?: boolean;
  optionalFields?: string[];
}

const formatZipCode = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, ZIP_CODE_MAX_DIGITS);
  if (digits.length <= ZIP_CODE_BASE_DIGITS) return digits;
  return `${digits.slice(0, ZIP_CODE_BASE_DIGITS)}-${digits.slice(ZIP_CODE_BASE_DIGITS)}`;
};

const AddressSection = <T extends FormikValues>({
  formik,
  formikKey,
  title = "Address",
  required = false,
  optionalFields = [],
}: AddressSectionProps<T>) => {
  const getName = (field: string) => `${formikKey}.${field}`;
  const getValue = (field: string) =>
    getIn(formik.values, getName(field)) ?? "";
  const getError = (field: string) =>
    getIn(formik.touched, getName(field)) &&
    getIn(formik.errors, getName(field));

  const renderInput = (field: string, label: string) => (
    <Input
      required={required && !optionalFields.includes(field)}
      label={label}
      textFieldProps={{
        placeholder: label,
        name: getName(field),
        value: getValue(field),
        onChange: formik.handleChange,
        onBlur: formik.handleBlur,
      }}
      error={getError(field)}
    />
  );

  return (
    <FormSectionCard title={title}>
      <Grid item xs={12} md={6}>
        {renderInput("line1", "Address Line 1")}
      </Grid>
      <Grid item xs={12} md={6}>
        {renderInput("line2", "Address Line 2")}
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        {renderInput("city", "City")}
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <CustomSelect
          required={required}
          label="State"
          placeholder="Select"
          name={getName("state")}
          options={STATE_LIST}
          value={getValue("state")}
          onChange={(value) => formik.setFieldValue(getName("state"), value)}
          handleBlur={formik.handleBlur}
          error={getError("state")}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <CustomSelect
          required={required}
          label="Country"
          placeholder="Country"
          disableSortOptions
          name={getName("country")}
          options={COUNTRIES_LIST}
          value={getValue("country")}
          onChange={(value) => formik.setFieldValue(getName("country"), value)}
          handleBlur={formik.handleBlur}
          error={getError("country")}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Input
          required={required}
          label="ZIP Code"
          textFieldProps={{
            placeholder: "ZIP Code",
            name: getName("zipcode"),
            value: formatZipCode(getValue("zipcode")),
            onChange: (e) =>
              formik.setFieldValue(
                getName("zipcode"),
                formatZipCode(e.target.value)
              ),
            onBlur: formik.handleBlur,
            inputProps: { maxLength: ZIP_CODE_MAX_LENGTH },
          }}
          error={getError("zipcode")}
        />
      </Grid>
    </FormSectionCard>
  );
};

export default AddressSection;
